/**
 * Popup de um GRUPO de embarcações (o selo com a contagem, visto de longe).
 *
 * Um grupo não tem ficha: mostrar a de um membro qualquer — que é o que a
 * primeira versão fazia — deixava a pessoa achando que clicou "no navio
 * errado". O grupo oferece a única ação que faz sentido nele: aproximar até
 * a resolução em que as embarcações se separam.
 */
import { ZoomIn } from 'lucide-react';
import { boundingExtent, buffer } from 'ol/extent';
import { useMapInstance } from '../map/mapContext';
import { Button } from '../ui/primitives';
import { RESOLUCAO_AGRUPA, type GrupoFeature } from './layer';
import { NOME_FAMILIA, tipoDe } from './kinds';

export function ClusterPopup({ grupo }: { grupo: GrupoFeature }) {
  const map = useMapInstance((s) => s.map);

  // Resumo por família, para o grupo dizer o que tem sem abrir cada ficha.
  const porFamilia = new Map<string, number>();
  for (const d of grupo.identificados) {
    const f = NOME_FAMILIA[tipoDe(d.navio.ficha?.tipo ?? null).familia];
    porFamilia.set(f, (porFamilia.get(f) ?? 0) + 1);
  }
  const resumo = [...porFamilia].sort((a, b) => b[1] - a[1]).slice(0, 3);
  const nomes = grupo.identificados
    .map((d) => d.navio.ficha?.nome)
    .filter((n): n is string => !!n)
    .slice(0, 4);

  function aproximar() {
    if (!map || grupo.coordenadas.length === 0) return;
    const extensao = buffer(boundingExtent(grupo.coordenadas), RESOLUCAO_AGRUPA * 8);
    // A folga em torno dos membros garante que o enquadramento fique abaixo
    // de `RESOLUCAO_AGRUPA` (troca para a camada sem agrupamento); o piso de
    // 40 m/px evita mergulhar até a escala de rua num grupo de dois barcos.
    map.getView().fit(extensao, { duration: 350, padding: [60, 60, 60, 60], minResolution: 40 });
  }

  return (
    <div className="space-y-1.5">
      <div className="text-xs text-ink-400">Embarcações · AIS</div>
      <div className="leading-tight font-semibold text-white">{grupo.total} embarcações aqui</div>
      {resumo.length > 0 && (
        <div className="text-xs text-ink-300">{resumo.map(([f, n]) => `${n} ${f.toLowerCase()}`).join(' · ')}</div>
      )}
      {nomes.length > 0 && (
        <div className="text-[11px] text-ink-400">
          {nomes.join(', ')}
          {grupo.identificados.length > nomes.length ? '…' : ''}
        </div>
      )}
      <Button icon={ZoomIn} onClick={aproximar}>
        Aproximar para ver uma a uma
      </Button>
    </div>
  );
}
